import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Users, UserPlus, Check, X, Clock, Search, ChevronRight,
  Building2, Mail, Phone, AlertTriangle, Handshake, Shield,
  Home, Loader2, MessageCircle
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AlliancesPage() {
  const [activeTab, setActiveTab] = useState('active');
  const [alliances, setAlliances] = useState([]);
  const [pendingAlliances, setPendingAlliances] = useState([]);
  const [sentAlliances, setSentAlliances] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAgreement, setShowAgreement] = useState(null);
  const [agreementTerms, setAgreementTerms] = useState('');
  
  const token = localStorage.getItem('upim_token');

  useEffect(() => {
    loadData();
    loadAgreementTerms();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      const [activeRes, pendingRes, sentRes] = await Promise.all([
        axios.get(`${API_URL}/api/alliances/active`, { headers }),
        axios.get(`${API_URL}/api/alliances/pending`, { headers }),
        axios.get(`${API_URL}/api/alliances/sent`, { headers })
      ]);
      
      setAlliances(activeRes.data.alliances || []);
      setPendingAlliances(pendingRes.data.pending_alliances || []);
      setSentAlliances(sentRes.data.sent_alliances || []);
    } catch (err) {
      console.error('Error loading alliances:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAgreementTerms = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/alliances/agreement-terms`);
      setAgreementTerms(res.data.terms);
    } catch (err) {
      console.error('Error loading terms:', err);
    }
  };

  const searchMembers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      const res = await axios.get(`${API_URL}/api/alliances/searchable-members?query=${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(res.data.members || []);
    } catch (err) {
      console.error('Error searching:', err);
    }
  };

  const sendAllianceRequest = async (receiverId) => {
    try {
      await axios.post(`${API_URL}/api/alliances/request`, 
        { receiver_id: receiverId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Solicitud de alianza enviada');
      setSearchResults([]);
      setSearchQuery('');
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al enviar solicitud');
    }
  };

  const respondToAlliance = async (allianceId, accept) => {
    try {
      await axios.post(`${API_URL}/api/alliances/${allianceId}/respond`,
        { accept },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowAgreement(null);
      loadData();
    } catch (err) {
      alert('Error al responder');
    }
  };

  const cancelAlliance = async (allianceId) => {
    if (!window.confirm('¿Estás seguro de cancelar esta alianza?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/alliances/${allianceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadData();
    } catch (err) {
      alert('Error al cancelar alianza');
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Handshake className="w-8 h-8 text-indigo-600" />
                Alianzas Comerciales
              </h1>
              <p className="text-gray-500 mt-1">Colabora con otros profesionales UPIM</p>
            </div>
          </div>

          {/* Search for new allies */}
          <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
            <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-indigo-600" />
              Buscar Aliados
            </h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nombre o email..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  searchMembers(e.target.value);
                }}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-4 space-y-2">
                {searchResults.map(member => (
                  <div 
                    key={member.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{member.name}</p>
                        <p className="text-sm text-gray-500">{member.email}</p>
                      </div>
                    </div>
                    {member.alliance_status === 'accepted' ? (
                      <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                        Ya es aliado
                      </span>
                    ) : member.alliance_status === 'pending' ? (
                      <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm">
                        Pendiente
                      </span>
                    ) : (
                      <button
                        onClick={() => sendAllianceRequest(member.id)}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                      >
                        Enviar solicitud
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto">
            {[
              { id: 'active', name: 'Aliados Activos', count: alliances.length, icon: Check },
              { id: 'pending', name: 'Solicitudes Recibidas', count: pendingAlliances.length, icon: Clock },
              { id: 'sent', name: 'Solicitudes Enviadas', count: sentAlliances.length, icon: ChevronRight },
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.name}
                  {tab.count > 0 && (
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      activeTab === tab.id ? 'bg-white/20' : 'bg-gray-200'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="space-y-4">
            {activeTab === 'active' && (
              alliances.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center">
                  <Handshake className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">Sin alianzas activas</h3>
                  <p className="text-gray-500">Busca otros profesionales UPIM para formar alianzas comerciales</p>
                </div>
              ) : (
                alliances.map(alliance => (
                  <div key={alliance.id} className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                          {alliance.ally.name?.charAt(0) || 'A'}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{alliance.ally.name}</h3>
                          <p className="text-sm text-gray-500">{alliance.ally.email}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            Aliado desde {new Date(alliance.accepted_at).toLocaleDateString('es-MX')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Link
                          to={`/alianzas/${alliance.id}/propiedades`}
                          className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 text-sm flex items-center gap-2"
                        >
                          <Home className="w-4 h-4" />
                          Ver propiedades ({alliance.shared_properties_count})
                        </Link>
                        <button
                          onClick={() => cancelAlliance(alliance.id)}
                          className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )
            )}

            {activeTab === 'pending' && (
              pendingAlliances.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center">
                  <Clock className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">Sin solicitudes pendientes</h3>
                  <p className="text-gray-500">No tienes solicitudes de alianza por revisar</p>
                </div>
              ) : (
                pendingAlliances.map(alliance => (
                  <div key={alliance.id} className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                          {alliance.requester.name?.charAt(0) || 'R'}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{alliance.requester.name}</h3>
                          <p className="text-sm text-gray-500">{alliance.requester.email}</p>
                          {alliance.message && (
                            <p className="text-sm text-gray-600 mt-1 italic">"{alliance.message}"</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowAgreement(alliance)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm flex items-center gap-2"
                        >
                          <Check className="w-4 h-4" />
                          Revisar y Aceptar
                        </button>
                        <button
                          onClick={() => respondToAlliance(alliance.id, false)}
                          className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 text-sm flex items-center gap-2"
                        >
                          <X className="w-4 h-4" />
                          Rechazar
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )
            )}

            {activeTab === 'sent' && (
              sentAlliances.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center">
                  <ChevronRight className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">Sin solicitudes enviadas</h3>
                  <p className="text-gray-500">No has enviado solicitudes de alianza</p>
                </div>
              ) : (
                sentAlliances.map(alliance => (
                  <div key={alliance.id} className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 font-bold text-xl">
                          {alliance.receiver.name?.charAt(0) || 'R'}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{alliance.receiver.name}</h3>
                          <p className="text-sm text-gray-500">{alliance.receiver.email}</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm ${
                        alliance.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        alliance.status === 'accepted' ? 'bg-green-100 text-green-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {alliance.status === 'pending' ? 'Pendiente' :
                         alliance.status === 'accepted' ? 'Aceptada' : 'Rechazada'}
                      </span>
                    </div>
                  </div>
                ))
              )
            )}
          </div>

          {/* Info Banner */}
          <div className="mt-8 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Alianzas Estratégicas UPIM</h3>
                <p className="text-indigo-100 text-sm leading-relaxed">
                  Las alianzas entre asociados UPIM te permiten ofrecer propiedades de colegas a tus clientes, 
                  evitando perder oportunidades. Todos los aliados están protegidos por el mismo marco jurídico 
                  y estándares de profesionalismo de la asociación.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Agreement Modal */}
      {showAgreement && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b bg-amber-50">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
                <h2 className="text-xl font-bold text-gray-900">Términos de Alianza</h2>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Lee cuidadosamente antes de aceptar la alianza con {showAgreement.requester.name}
              </p>
            </div>
            
            <div className="p-6 max-h-[50vh] overflow-y-auto">
              <div className="prose prose-sm">
                <div 
                  className="whitespace-pre-wrap text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ 
                    __html: (showAgreement.agreement_terms || agreementTerms)
                      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      .replace(/\n/g, '<br/>')
                  }}
                />
              </div>
            </div>
            
            <div className="p-6 border-t bg-gray-50 flex gap-3 justify-end">
              <button
                onClick={() => setShowAgreement(null)}
                className="px-6 py-2 text-gray-600 hover:bg-gray-200 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={() => respondToAlliance(showAgreement.id, true)}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                Acepto los Términos
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
